import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { userSpecificInfo, updateUserInfo, addTrip, updatePassengerData, deletePassengerData } from '../services/api';
import PassengerDetails from '../components/PassengerDetails';
// import { formatDateTime } from '../utils/dateUtils'; // Not currently used

// Helper function to format date in a readable format without timezone conversion
const formatDate = (dateString) => {
  if (!dateString) {
    return 'N/A';
  }

  try {
    // Parse date and time parts separately to avoid timezone conversion
    const [datePart, timePart] = dateString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const timeOnly = timePart ? timePart.replace('Z', '').split('.')[0] : '00:00:00';
    const [hours, minutes, seconds = 0] = timeOnly.split(':').map(Number);

    // Create date object without timezone conversion
    const date = new Date(year, month - 1, day, hours, minutes, seconds);

    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString || 'N/A';
  }
};

// CSS Styles for tabbed interface
const styles = `
  .content-layout {
    display: flex;
    width: 100%;
    overflow-x: hidden;
  }

  .content-container {
    display: flex;
    flex: 1 1 0%;
    position: relative;
    height: 100vh;
    overflow: hidden;
    max-height: 100vh;
  }

  .main-content {
    flex: 1 1 0%;
    padding: 1.5rem 0 1.5rem 1.5rem;
    overflow-x: hidden;
    overflow-y: auto;
    height: 100vh;
    max-height: 100vh;
    position: relative;
    display: flex;
    flex-direction: column;
  }

  .sidebar {
    width: 350px;
    padding: 1.5rem;
    border-left: 1px solid #e5e7eb;
    position: sticky;
    top: 0;
    height: 100vh;
    max-height: 100vh;
    overflow-y: auto;
    flex-shrink: 0;
    background-color: white;
    box-shadow: -4px 0 10px rgba(0, 0, 0, 0.03);
    align-self: flex-start;
  }

  .tab-nav-container {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: white;
    width: 100%;
    padding-top: 1rem;
    flex-shrink: 0;
  }

  .tab-list {
    display: flex;
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .tab-list::-webkit-scrollbar {
    display: none;
  }

  .tab-content-container {
    padding: 1rem 0;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }

  .tab-content {
    display: none;
    animation: fadeIn 0.3s ease-in-out;
  }

  .tab-content.active {
    display: block;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @media (max-width: 1024px) {
    .content-container {
      flex-direction: column;
      height: auto;
    }
    .sidebar {
      width: 100%;
      height: auto;
      position: relative;
      border-left: none;
      border-top: 1px solid #e5e7eb;
    }
    .main-content {
      height: auto;
      max-height: none;
    }
  }
`;

/**
 * User Info Page component
 * @returns {JSX.Element} UserInfoPage component
 */
const UserInfoPage = () => {
  const { id } = useParams(); // Get the user ID from the URL
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('whatsapp'); // Default to WhatsApp tab (original behavior)

  // Edit functionality state
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    mobileNumber: ''
  });
  const [updateLoading, setUpdateLoading] = useState(false);

  // Add flight modal state
  const [showAddFlightModal, setShowAddFlightModal] = useState(false);
  const [addFlightFormData, setAddFlightFormData] = useState({
    flightNumber: '',
    departureDate: '',
    departureIata: '',
    arrivalIata: ''
  });
  const [addFlightLoading, setAddFlightLoading] = useState(false);
  const [addFlightError, setAddFlightError] = useState('');

  // Get flights data from API response instead of mock data and sort by departure date (DESC - most recent first)
  const flightsData = (userData?.flightsData || []).sort((a, b) => {
    // Handle cases where departure_date might be missing
    const dateA = a.departure_date || a.departureTime || '';
    const dateB = b.departure_date || b.departureTime || '';

    // Sort in descending order (most recent first)
    return new Date(dateB) - new Date(dateA);
  });

  // Get passengers data from API response and transform it to match expected format
  const passengersData = userData?.passengersData || [];

  // Transform API passenger data to match the expected format for PassengerDetails component
  const transformedPassengers = passengersData.map((passenger) => ({
    id: passenger.passengerId,
    passengerFlightId: passenger.passengerFlightId, // Use actual passengerFlightId from API
    first_name: passenger.firstName || '',
    last_name: passenger.lastName || '',
    gender: passenger.gender || '',
    date_of_birth: passenger.dateOfBirth || '',
    // For identification document data, using empty strings as requested
    passport_number: '',
    passport_expiry: '',
    nationality: '',
    issuing_country: '',
    // Add passenger documents from API
    passengerDocuments: passenger.passengerDocuments || []
  }));

  // Fetch user data when component mounts or ID changes
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        console.log('Fetching user data for user ID:', id);

        const data = await userSpecificInfo(id);
        console.log('API Response data:', data);

        setUserData(data);
        setError(null);
      } catch (err) {
        console.error('API Error:', err);
        setError(err.message || 'An error occurred while fetching user data');
        setUserData(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchUserData();
    }
  }, [id]);

  // Handle back button click (currently commented out in UI)
  // Keeping this function for future use
  // const handleBack = () => {
  //   navigate('/users');
  // };

  // Helper function to handle tab clicks
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  // Helper function to open add flight modal
  const handleAddNewFlight = () => {
    setShowAddFlightModal(true);
    setAddFlightError('');
  };

  // Helper function to close add flight modal
  const handleCloseAddFlightModal = () => {
    setShowAddFlightModal(false);
    setAddFlightFormData({
      flightNumber: '',
      departureDate: '',
      departureIata: '',
      arrivalIata: ''
    });
    setAddFlightError('');
  };

  // Helper function to handle add flight form input changes
  const handleAddFlightInputChange = (field, value) => {
    setAddFlightFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helper function to submit add flight form
  const handleSubmitAddFlight = async () => {
    try {
      // Validate required fields
      if (!addFlightFormData.flightNumber.trim()) {
        setAddFlightError('Flight number is required');
        return;
      }
      if (!addFlightFormData.departureDate.trim()) {
        setAddFlightError('Departure date is required');
        return;
      }

      setAddFlightLoading(true);
      setAddFlightError('');

      // Call the API to add the trip
      await addTrip({
        userId: parseInt(id),
        flightNumber: addFlightFormData.flightNumber.trim(),
        departureDate: addFlightFormData.departureDate,
        departureIata: addFlightFormData.departureIata.trim(),
        arrivalIata: addFlightFormData.arrivalIata.trim()
      });

      // Refresh user data to get updated flights
      const updatedData = await userSpecificInfo(id);
      setUserData(updatedData);

      // Close modal and show success message
      handleCloseAddFlightModal();
      alert('Flight added successfully!');

    } catch (error) {
      console.error('Error adding flight:', error);
      setAddFlightError(error.message);
    } finally {
      setAddFlightLoading(false);
    }
  };

  // Helper function to handle flight click navigation
  const handleFlightClick = (ticketId, userId) => {
    navigate(`/check-in/${ticketId}/${userId}`);
  };

  // Helper function to start editing
  const handleStartEdit = () => {
    if (userData?.userData) {
      setEditFormData({
        firstName: userData.userData.firstName || '',
        lastName: userData.userData.lastName || '',
        mobileNumber: userData.userData.mobileNumber || ''
      });
      setIsEditing(true);
    }
  };

  // Helper function to cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({
      firstName: '',
      lastName: '',
      mobileNumber: ''
    });
  };

  // Helper function to handle form input changes
  const handleInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helper function to save user information
  const handleSaveUserInfo = async () => {
    try {
      setUpdateLoading(true);

      // Call the API to update user information
      await updateUserInfo(editFormData, parseInt(id));

      // Refresh user data after successful update
      const updatedData = await userSpecificInfo(id);
      setUserData(updatedData);

      // Exit edit mode
      setIsEditing(false);

      // Show success message (you can replace this with a toast notification)
      alert('User information updated successfully!');

    } catch (error) {
      console.error('Error updating user information:', error);
      alert('Failed to update user information: ' + error.message);
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <MainLayout activeTab="users">
      <style>{styles}</style>

      {/* Main Content Area */}
      {loading ? (
        <div className="flex justify-center items-center h-screen w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="w-full p-4">
          <div className="bg-red-100 text-red-700 p-4 rounded-md">
            <p>{error}</p>
          </div>
        </div>
      ) : (
        <div className="content-container">
          {/* Main content area with tabs */}
          <div className="main-content" style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-gray-900">User Info</h1>
              <p className="text-gray-600">User ID: {id}</p>
            </div>

            {/* Tabs - Sticky */}
            <div className="tab-nav-container">
              <ul className="tab-list">
                <li className="mr-2">
                  <button
                    className={`inline-flex items-center py-4 px-4 text-sm font-medium ${
                      activeTab === "whatsapp"
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                    onClick={() => handleTabClick("whatsapp")}
                  >
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-100 text-green-500 mr-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                      </svg>
                    </span>
                    WhatsApp
                    {userData && userData.whatsapp && userData.whatsapp.length > 0 && (
                      <span className="ml-2 bg-gray-100 text-gray-700 py-0.5 px-2 rounded-full text-xs">
                        {userData.whatsapp.length}
                      </span>
                    )}
                  </button>
                </li>
                <li className="mr-2">
                  <button
                    className={`inline-flex items-center py-4 px-4 text-sm font-medium ${
                      activeTab === "push"
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                    onClick={() => handleTabClick("push")}
                  >
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-100 text-blue-500 mr-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                      </svg>
                    </span>
                    Push Notifications
                    {userData && userData.pushNotification && userData.pushNotification.length > 0 && (
                      <span className="ml-2 bg-gray-100 text-gray-700 py-0.5 px-2 rounded-full text-xs">
                        {userData.pushNotification.length}
                      </span>
                    )}
                  </button>
                </li>
                <li className="mr-2">
                  <button
                    className={`inline-flex items-center py-4 px-4 text-sm font-medium ${
                      activeTab === "passengers"
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                    onClick={() => handleTabClick("passengers")}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                    </svg>
                    Passengers
                  </button>
                </li>
                <li className="mr-2">
                  <button
                    className={`inline-flex items-center py-4 px-4 text-sm font-medium ${
                      activeTab === "userDetails"
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                    onClick={() => handleTabClick("userDetails")}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    User Details
                  </button>
                </li>
                <li className="mr-2">
                  <button
                    className={`inline-flex items-center py-4 px-4 text-sm font-medium ${
                      activeTab === "flights"
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                    onClick={() => handleTabClick("flights")}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                    </svg>
                    Flights
                  </button>
                </li>
              </ul>
            </div>

            {/* Tab Content */}
            <div className="tab-content-container">
              {/* WhatsApp Notifications Tab Content */}
              <div
                id="whatsapp"
                className={`tab-content ${
                  activeTab === "whatsapp" ? "active" : ""
                }`}
              >
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-xl font-bold mb-6">WhatsApp Messages</h2>
                  {userData && userData.whatsapp && userData.whatsapp.length > 0 ? (
                    <div className="space-y-4">
                      {userData.whatsapp.map((message, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center">
                              <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-green-100 text-green-500 mr-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                                </svg>
                              </span>
                              <span className="font-medium text-gray-900">{message.templateName || 'Unnamed Template'}</span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {formatDate(message.createdAt)}
                            </span>
                          </div>
                          <div className="ml-11">
                            <p className="text-sm text-gray-500 mb-2">Phone: {message.phoneNumber || 'N/A'}</p>
                            <div className="bg-white p-3 rounded-lg border border-gray-200 mb-2">
                              {message.components && message.components.map((component, compIndex) => (
                                <div key={compIndex} className="mb-1">
                                  {component && component.type === 'text' && (
                                    <p className="text-sm whitespace-pre-line">{component.text || ''}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-500">Message ID: {message.messageId ? message.messageId.substring(0, 15) + '...' : 'N/A'}</span>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                message.statusCode === 200 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                Status: {message.statusCode === 200 ? 'Delivered' : 'Failed'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <p className="text-gray-600">No WhatsApp messages found for this user.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Push Notifications Tab Content */}
              <div
                id="push"
                className={`tab-content ${
                  activeTab === "push" ? "active" : ""
                }`}
              >
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-xl font-bold mb-6">Push Notifications</h2>
                  {userData && userData.pushNotification && userData.pushNotification.length > 0 ? (
                    <div className="space-y-4">
                      {userData.pushNotification.map((notification, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center">
                              <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-500 mr-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                </svg>
                              </span>
                              <span className="font-medium text-gray-900">{notification.type || 'Unknown Type'}</span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {formatDate(notification.createdAt)}
                            </span>
                          </div>
                          <div className="ml-11">
                            <p className="text-sm text-gray-500 mb-2">Device: {notification.deviceToken ? notification.deviceToken.substring(0, 12) + '...' : 'N/A'}</p>
                            <div className="bg-white p-3 rounded-lg border border-gray-200 mb-2">
                              {notification.payload && notification.payload.contents && (
                                <div className="mb-1">
                                  <p className="text-sm font-medium text-gray-700">Content:</p>
                                  {Object.entries(notification.payload.contents || {}).map(([lang, content], i) => (
                                    <p key={i} className="text-sm whitespace-pre-line">
                                      <span className="text-xs text-gray-500">{lang}: </span>
                                      {content || ''}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <p className="text-gray-600">No push notifications found for this user.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Passengers Tab */}
              <div
                id="passengers"
                className={`tab-content ${
                  activeTab === "passengers" ? "active" : ""
                }`}
              >
                <PassengerDetails
                  passengers={transformedPassengers}
                  onUpdatePassenger={updatePassengerData}
                  onDeletePassenger={deletePassengerData}
                  onRefresh={() => {
                    // Refresh user data after passenger operations
                    userSpecificInfo(id).then(setUserData).catch(console.error);
                  }}
                />
              </div>

              {/* User Details Tab */}
              <div
                id="userDetails"
                className={`tab-content ${
                  activeTab === "userDetails" ? "active" : ""
                }`}
              >
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold">User Information</h2>
                    {!isEditing && (
                      <button
                        onClick={handleStartEdit}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                        Edit
                      </button>
                    )}
                  </div>
                  {userData ? (
                    <div className="space-y-6">
                      {/* Personal Information */}
                      <div>
                        <h3 className="text-lg font-semibold mb-4 text-gray-900">Personal Information</h3>
                        {isEditing ? (
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                                <input
                                  type="text"
                                  value={editFormData.firstName}
                                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Enter first name"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                                <input
                                  type="text"
                                  value={editFormData.lastName}
                                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Enter last name"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                                <input
                                  type="text"
                                  value={editFormData.mobileNumber}
                                  onChange={(e) => handleInputChange('mobileNumber', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Enter mobile number"
                                />
                              </div>
                            </div>
                            <div className="flex justify-end space-x-3 pt-4">
                              <button
                                onClick={handleCancelEdit}
                                disabled={updateLoading}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleSaveUserInfo}
                                disabled={updateLoading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                              >
                                {updateLoading ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                    Saving...
                                  </>
                                ) : (
                                  'Save Changes'
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-3">
                              <div className="flex justify-between p-3 bg-gray-50 rounded-md">
                                <span className="text-sm text-gray-500">First Name</span>
                                <span className="font-medium">{userData.userData?.firstName || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between p-3 bg-gray-50 rounded-md">
                                <span className="text-sm text-gray-500">Last Name</span>
                                <span className="font-medium">{userData.userData?.lastName || 'N/A'}</span>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <div className="flex justify-between p-3 bg-gray-50 rounded-md">
                                <span className="text-sm text-gray-500">Email(s)</span>
                                <div className="font-medium text-right">
                                  {userData.userData?.emails && userData.userData.emails.length > 0 ? (
                                    userData.userData.emails.map((email, index) => (
                                      <div key={index} className="text-sm">
                                        {email}
                                      </div>
                                    ))
                                  ) : (
                                    'N/A'
                                  )}
                                </div>
                              </div>
                              <div className="flex justify-between p-3 bg-gray-50 rounded-md">
                                <span className="text-sm text-gray-500">Mobile Number</span>
                                <span className="font-medium">{userData.userData?.mobileNumber || 'N/A'}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Notification Preferences */}
                      <div>
                        <h3 className="text-lg font-semibold mb-4 text-gray-900">Notification History</h3>
                        <div className="space-y-4">
                          {/* WhatsApp Notifications */}
                          <div className="border border-gray-200 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-green-100 text-green-500 mr-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                                </svg>
                              </span>
                              WhatsApp Messages
                              {userData.whatsapp && userData.whatsapp.length > 0 && (
                                <span className="ml-2 bg-gray-100 text-gray-700 py-0.5 px-2 rounded-full text-xs">
                                  {userData.whatsapp.length}
                                </span>
                              )}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {userData.whatsapp && userData.whatsapp.length > 0
                                ? `${userData.whatsapp.length} WhatsApp messages sent`
                                : 'No WhatsApp messages sent'}
                            </p>
                          </div>

                          {/* Push Notifications */}
                          <div className="border border-gray-200 rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-500 mr-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                </svg>
                              </span>
                              Push Notifications
                              {userData.pushNotification && userData.pushNotification.length > 0 && (
                                <span className="ml-2 bg-gray-100 text-gray-700 py-0.5 px-2 rounded-full text-xs">
                                  {userData.pushNotification.length}
                                </span>
                              )}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {userData.pushNotification && userData.pushNotification.length > 0
                                ? `${userData.pushNotification.length} push notifications sent`
                                : 'No push notifications sent'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500">No user information available</p>
                  )}
                </div>
              </div>

              {/* Flights Tab */}
              <div
                id="flights"
                className={`tab-content ${
                  activeTab === "flights" ? "active" : ""
                }`}
              >
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold">Flights</h2>
                    <button
                      onClick={handleAddNewFlight}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                      Add New Flight
                    </button>
                  </div>

                  {flightsData.length > 0 ? (
                    <div className="space-y-4">
                      {flightsData.map((flight) => (
                        <div
                          key={flight.flightId}
                          className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => handleFlightClick(flight.ticketId, id)}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center">
                              <div className="bg-blue-100 p-2 rounded-lg mr-3">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                                </svg>
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900">{flight.flightNumber}</h3>
                                <p className="text-sm text-gray-600">{flight.airline}</p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end space-y-1">
                              <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                {flight.ticketSource || 'N/A'}
                              </span>
                              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                {flight.belongsTo || 'N/A'}
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                            <div>
                              <p className="text-sm text-gray-500">Departure</p>
                              <p className="font-medium">{flight.departureAirport}</p>
                              <p className="text-sm text-gray-600">{formatDate(flight.departureTime)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Arrival</p>
                              <p className="font-medium">{flight.arrivalAirport}</p>
                              <p className="text-sm text-gray-600">{formatDate(flight.arrivalTime)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Flight Details</p>
                              <p className="font-medium">Flight ID: {flight.flightId}</p>
                              <p className="text-sm text-gray-600">Ticket ID: {flight.ticketId}</p>
                              {flight.pnr && <p className="text-sm text-gray-600">PNR: {flight.pnr}</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      <p className="text-gray-600">No flights found for this user.</p>
                      <button
                        onClick={handleAddNewFlight}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Add First Flight
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right sidebar with user summary */}
          <div className="sidebar">
            <div className="sticky top-0">
              <h2 className="text-xl font-bold mb-6">User Summary</h2>

              {userData ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">Quick Stats</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Passengers:</span>
                        <span className="text-sm font-medium">{transformedPassengers.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Flights:</span>
                        <span className="text-sm font-medium">{flightsData.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">WhatsApp Messages:</span>
                        <span className="text-sm font-medium">{userData.whatsapp?.length || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Push Notifications:</span>
                        <span className="text-sm font-medium">{userData.pushNotification?.length || 0}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2">User ID</h3>
                    <p className="text-blue-800 font-mono text-sm">{id}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-600">Loading user summary...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Flight Modal */}
      {showAddFlightModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add New Flight</h3>
              <button
                onClick={handleCloseAddFlightModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {addFlightError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {addFlightError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Flight Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={addFlightFormData.flightNumber}
                  onChange={(e) => handleAddFlightInputChange('flightNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., QP1407"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Departure Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={addFlightFormData.departureDate}
                  onChange={(e) => handleAddFlightInputChange('departureDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Departure IATA (Optional)
                </label>
                <input
                  type="text"
                  value={addFlightFormData.departureIata}
                  onChange={(e) => handleAddFlightInputChange('departureIata', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., DEL"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Arrival IATA (Optional)
                </label>
                <input
                  type="text"
                  value={addFlightFormData.arrivalIata}
                  onChange={(e) => handleAddFlightInputChange('arrivalIata', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., BOM"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleCloseAddFlightModal}
                disabled={addFlightLoading}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAddFlight}
                disabled={addFlightLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {addFlightLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Adding...
                  </>
                ) : (
                  'Add Flight'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default UserInfoPage;
